import { useEffect, useMemo } from 'react';
import { Button } from '@components/button';
import { Box, Grid, Typography } from '@mui/material';
import { getNumbers } from '@utils/regex';
import { Item, Select } from '@components/select/Select';
import { TextField } from '@components/textField/TextField';
import { ValidationError } from '@models/quoteItem';
import { AttributeType } from '@store/Quote/Quote.types';
import { getMaskedStyles, StyledIMaskInput } from '@components/quoteDetails/QuoteDetails.styles';
import { getErrorMessage } from '@utils/validation';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Autocomplete } from '@components/autocomplete';
import { useGetBrokerageAppointments } from '@services/brokerage';
import { useQuote } from '@services/quotes';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { BindCard } from '@components/bindCard/BindCard';
import { engineRegExp, QuoteType } from '@lio/rule-engine';
import { ProductOfferingId } from '@models/newQuote';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { useApiEnvStore } from '@utils/api-client';
import { DebugInformationButtonModal } from '@components/debugModeControls/DebugInformationButtonModal';

export type BillingAddressAutofillType = 'Business' | 'Mailing' | '';
export type Field = {
  value: any;
  codeName: string;
};
export type BillingCardFields = Record<string, Field>;

export type BillingCardProps = {
  onFieldsChange: (validated: boolean) => void;
  attributeTypes: Record<
    string,
    AttributeType & {
      key: string;
      options?: { value: string; label: string }[];
      label: string | null;
      isVisible: boolean;
    }
  >;
  hasUnsavedChanges: boolean;
  onSaveQuote: () => void;
  isQuoteItemAttributesLoading: boolean;
  onUpdateBillingMethod: (newValue: string) => void;
  errorsByCodeName: Record<string, ValidationError>;
  fieldsDisabled: boolean;
};

export const BillingCard = ({
  onFieldsChange,
  attributeTypes,
  hasUnsavedChanges,
  onSaveQuote,
  isQuoteItemAttributesLoading,
  fieldsDisabled,
  onUpdateBillingMethod,
  errorsByCodeName,
}: BillingCardProps) => {
  const { quoteId } = useParams<{ quoteId: string }>();
  invariant(quoteId);
  const { quote } = useQuote(quoteId);
  const writingCompanyId = quote?.writingCompany?.id;
  const brokerageId = quote?.brokerage?.id ?? null;

  const [{ isLioSales, isInternalUser, isCustomerUser }] = useStore((state) => [state.userAuth], shallow);
  const [fieldValues, setFieldValues, businessAddress, setAutocompleteAddress] = useQuoteStore(
    (state) => [
      state.billingCardFields,
      state.setBillingCardFields,
      state.businessAddress,
      state.setAutoCompleteAddress,
    ],
    shallow,
  );
  const [debugMode, debugModeOptions] = useApiEnvStore((state) => [state.debugMode, state.debugModeOptions], shallow);
  const { isLoading, brokerageAppointments } = useGetBrokerageAppointments({ brokerageId });
  const hasBusinessAddress = !!businessAddress;
  const isEndorsement = quote?.quoteType === QuoteType.Endorsement;
  const handleInputFieldChange = (key: string, value: string | boolean) => {
    if (key === attributeTypes.billingMethod.key && value !== fieldValues.billingMethod.value) {
      onUpdateBillingMethod(value as string);
    }

    const values = {
      ...fieldValues,
      [key]: {
        ...fieldValues[key],
        value,
      },
    };
    setFieldValues(values as BillingCardFields);
  };

  useEffect(() => {
    const endorsementBillingFieldsFilled = isEndorsement ? fieldValues.endorsementPaymentOption.value !== '' : true;
    if (
      (!attributeTypes.street?.isVisible && endorsementBillingFieldsFilled) ||
      (fieldValues.billingMethod.value === 'AgencyBill' && endorsementBillingFieldsFilled)
    ) {
      const areBillingFieldsFilled = true;
      onFieldsChange(areBillingFieldsFilled);
    } else {
      const areBillingFieldsFilled =
        fieldValues.street.value !== '' &&
        fieldValues.city.value !== '' &&
        fieldValues.state.value !== '' &&
        fieldValues.zip.value !== '' &&
        fieldValues.billPlan.value !== '' &&
        fieldValues.billingMethod.value !== '' &&
        endorsementBillingFieldsFilled;

      onFieldsChange(areBillingFieldsFilled);
    }
  }, [fieldValues, onFieldsChange, attributeTypes.street?.isVisible, isEndorsement]);

  const showBillPlan =
    !!attributeTypes.billPlan?.isVisible &&
    (fieldValues.billingMethod.value === 'DirectBill' || (attributeTypes.billPlan.options ?? []).length > 0);

  const showBillingMethod = attributeTypes.billingMethod.isVisible;
  let showAddressFields =
    !!attributeTypes.street?.isVisible &&
    fieldValues.billingMethod.value &&
    fieldValues.billingMethod.value !== 'AgencyBill';
  const showEndorsementBillingOption = isEndorsement && attributeTypes?.endorsementPaymentOption?.isVisible;
  let showBillingOptions = showBillPlan || showBillingMethod || showEndorsementBillingOption;
  let showBillingCard = (showBillingOptions || showAddressFields) && !isLoading;

  const handleErrorMessage = (codeName: string): string => {
    const error = errorsByCodeName[codeName];
    const errorMessage = getErrorMessage(error);
    return errorMessage;
  };

  const billingMethodOptions = useMemo(() => {
    if (brokerageAppointments && brokerageAppointments?.length > 0) {
      const options = brokerageAppointments
        ?.filter((ba) => ba.writingCompany.id === writingCompanyId)
        .map((a) => {
          let label: string = a.billingMethod;
          if (a.billingMethod === 'AgencyBill') {
            label = 'Agency Bill';
          }
          if (a.billingMethod === 'DirectBill') {
            label = 'Direct Bill';
          }
          return {
            value: a.billingMethod,
            label,
          };
        });

      return options?.length > 0 ? options : attributeTypes.billingMethod.options;
    }

    return attributeTypes.billingMethod.options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerageAppointments, writingCompanyId]);

  const isEmailValid = useMemo(
    () => engineRegExp.email.regExp.test(fieldValues?.emailAddress?.value ?? ''),
    [fieldValues?.emailAddress],
  );
  const isNotValid = !isEmailValid;

  const isValidBillPlan = useMemo(() => {
    return (
      attributeTypes.billPlan.options?.find((option) => option.value === fieldValues.billPlan.value) !== undefined &&
      attributeTypes.billPlan.options?.find((option) => option.value === attributeTypes.billPlan.attribute?.value) !==
        undefined
    );
  }, [attributeTypes.billPlan.options, attributeTypes.billPlan.attribute?.value, fieldValues.billPlan.value]);

  useEffect(() => {
    if (attributeTypes.billPlan.options?.length === 1) {
      const autoSelectOption = attributeTypes.billPlan.options[0].value;
      if (fieldValues.billPlan.value !== autoSelectOption) {
        handleInputFieldChange(attributeTypes.billPlan.key, autoSelectOption);
      }
    } else {
      const isValidBillPlanChange =
        attributeTypes.billPlan.options?.find((option) => option.value === fieldValues.billPlan.value) !== undefined;

      if (!isValidBillPlanChange) {
        handleInputFieldChange(attributeTypes.billPlan.key, '');
      }
    }
    // eslint-disable-next-line
  }, [
    attributeTypes.billPlan.key,
    attributeTypes.billPlan.options,
    attributeTypes.billPlan,
    fieldValues.billPlan.value,
  ]);

  if (!showBillingCard) {
    // Show debug card with field values when in debug mode
    if (debugMode && debugModeOptions.showExtraDetails) {
      return (
        <BindCard>
          {[
            {
              title: 'Debug Mode: Billing Card',
              content: (
                <DebugInformationButtonModal
                  title='Billing Card Field Values'
                  buttonText='View Billing Card Field Values'
                  jsonValue={fieldValues}
                  objectSortKeys
                  tooltipText={''}
                />
              ),
            },
          ]}
        </BindCard>
      );
    }
    return null;
  }

  const hideAddressSelectionButtons = isCustomerUser && quote?.offering?.id === ProductOfferingId.SpecialEvent;

  if (hideAddressSelectionButtons) {
    return null;
  }

  return (
    <Box id='review-billing-card' data-testid='review-billing-card'>
      <BindCard>
        {[
          showBillingOptions
            ? {
                title: 'Billing Options',
                content: (
                  <Grid
                    container
                    size={{ xs: 12, md: 11, lg: 9, xl: 7 }}
                    sx={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    {showBillingMethod && (
                      <Grid size={{ xs: 12 }}>
                        <Select
                          required
                          inputLabel={attributeTypes.billingMethod.label!}
                          placeholder='Please select'
                          data-testid='billingMethod'
                          items={billingMethodOptions}
                          value={fieldValues.billingMethod.value}
                          disabled={!isInternalUser || isLioSales || fieldsDisabled}
                          onChange={(event) =>
                            handleInputFieldChange(attributeTypes.billingMethod.key, event?.target.value)
                          }
                          fullWidth
                        />
                      </Grid>
                    )}
                    {showBillPlan && (
                      <Grid size={{ xs: isEndorsement ? 6 : 12 }} sx={{ marginTop: showBillingMethod ? '24px' : '0' }}>
                        <Select
                          required
                          inputLabel={attributeTypes.billPlan.label!}
                          placeholder='Please select'
                          data-testid='billPlan'
                          items={attributeTypes.billPlan.options}
                          value={fieldValues.billPlan.value}
                          disabled={(isEndorsement && isValidBillPlan) || isLioSales || fieldsDisabled}
                          onChange={(event) => handleInputFieldChange(attributeTypes.billPlan.key, event?.target.value)}
                          fullWidth
                          sx={{ marginRight: isEndorsement ? '10px' : '' }}
                        />
                      </Grid>
                    )}
                    {showEndorsementBillingOption && (
                      <Grid size={{ xs: 6 }} sx={{ marginTop: showBillingMethod ? '24px' : '0' }}>
                        <Select
                          required
                          helperLabel={handleErrorMessage(attributeTypes.endorsementPaymentOption.codeName)}
                          error={!!errorsByCodeName[attributeTypes.endorsementPaymentOption.codeName]}
                          inputLabel={attributeTypes.endorsementPaymentOption.label!}
                          placeholder='Please select'
                          data-testid='endorsementBillingOption'
                          items={attributeTypes.endorsementPaymentOption.options}
                          value={fieldValues.endorsementPaymentOption?.value}
                          onChange={(event) =>
                            handleInputFieldChange(attributeTypes.endorsementPaymentOption.key, event?.target.value)
                          }
                          sx={{
                            width: '100%',
                            marginBottom: handleErrorMessage(attributeTypes.endorsementPaymentOption.codeName)
                              ? '0'
                              : '24px',
                          }}
                          fullWidth
                        />
                      </Grid>
                    )}
                  </Grid>
                ),
              }
            : {},
          showAddressFields
            ? {
                title: 'Billing Address & Contact',
                content: (
                  <>
                    <Grid
                      container
                      size={{
                        xs: 12,
                        md: 11,
                        lg: 9,
                        xl: 7,
                      }}
                      sx={{
                        flexDirection: 'row',
                        marginBottom: 1,
                        justifyContent: 'space-evenly',
                        '@media(min-width: 2100px)': { justifyContent: 'flex-end' },
                      }}
                    >
                      <Button
                        data-testid='Use Mailing Address'
                        onClick={() => setAutocompleteAddress('Mailing')}
                        variant='outlined'
                        disabled={isLioSales || fieldsDisabled}
                        sx={{ width: '176px', marginBottom: 2, '@media(min-width: 2100px)': { marginRight: 2 } }}
                      >
                        Use Mailing Address
                      </Button>
                      <Button
                        data-testid='Use Business Address'
                        onClick={() => setAutocompleteAddress('Business')}
                        variant='outlined'
                        disabled={!hasBusinessAddress || fieldsDisabled}
                        sx={{ marginBottom: 2 }}
                      >
                        Use Business Address
                      </Button>
                    </Grid>
                    <Grid
                      container
                      size={{ xs: 12 }}
                      sx={{
                        width: '100%',
                        flexDirection: 'row',
                      }}
                    >
                      <Grid
                        container
                        size={{ xs: 12 }}
                        sx={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Grid size={{ xs: 12, md: 11, lg: 9, xl: 7 }} sx={{ marginBottom: 2 }}>
                          <TextField
                            inputLabel={attributeTypes.primaryContact.label}
                            value={fieldValues.primaryContact.value}
                            onChangeInput={(value) => handleInputFieldChange(attributeTypes.primaryContact.key, value)}
                            fullWidth
                            disabled={isLioSales || fieldsDisabled}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 11, lg: 9, xl: 7 }} sx={{ marginBottom: 2 }}>
                          <TextField
                            required
                            inputLabel={attributeTypes.street.label}
                            error={!!errorsByCodeName[attributeTypes.street.codeName]}
                            helperText={handleErrorMessage(attributeTypes.street.codeName)}
                            value={fieldValues.street.value}
                            onChangeInput={(value) => handleInputFieldChange(attributeTypes.street.key, value)}
                            fullWidth
                            disabled={isLioSales || fieldsDisabled}
                          />
                        </Grid>
                        <Grid size={{ md: 9, lg: 6 }} />
                        <Grid size={{ xs: 12, md: 11, lg: 9, xl: 7 }} sx={{ marginBottom: 2 }}>
                          <TextField
                            inputLabel={attributeTypes.street2.label}
                            value={fieldValues.street2.value}
                            onChangeInput={(value) => handleInputFieldChange(attributeTypes.street2.key, value)}
                            fullWidth
                            disabled={isLioSales || fieldsDisabled}
                          />
                        </Grid>
                        <Grid size={{ lg: 5 }} />
                        <Grid
                          container
                          size={{ xs: 12, md: 11, lg: 9, xl: 7 }}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          <Grid size={{ xs: 12, lg: 6 }} sx={{ marginBottom: 2 }}>
                            <TextField
                              required
                              error={!!errorsByCodeName[attributeTypes.city.codeName]}
                              helperText={handleErrorMessage(attributeTypes.city.codeName)}
                              inputLabel={attributeTypes.city.label}
                              value={fieldValues.city.value}
                              onChangeInput={(value) => handleInputFieldChange(attributeTypes.city.key, value)}
                              fullWidth
                              disabled={isLioSales || fieldsDisabled}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 5, lg: 2 }}
                            sx={{
                              marginBottom: 2,
                              '.MuiAutocomplete-clearIndicator': {
                                width: '5px !important' /* move clearable icon over since state dropdowns are tiny */,
                              },
                            }}
                          >
                            <Autocomplete
                              openOnFocus
                              data-testid='State'
                              fullWidth
                              options={attributeTypes.state.options ?? []}
                              value={fieldValues.state.value}
                              disabled={isLioSales || fieldsDisabled}
                              onChange={(_event, value: any) => {
                                handleInputFieldChange(attributeTypes.state.key, value?.value ?? undefined);
                              }}
                              renderOption={(props, option: Item) =>
                                option?.value === '' || option?.hidden ? null : (
                                  <li {...props} key={option?.value} data-testid={option.value}>
                                    <Typography
                                      variant='bodySmall'
                                      sx={{
                                        color: 'text.main',
                                      }}
                                    >
                                      {option.label}
                                    </Typography>
                                  </li>
                                )
                              }
                              renderInput={(params: any) => (
                                <TextField
                                  {...params}
                                  error={!!errorsByCodeName[attributeTypes.state.codeName]}
                                  helperLabel={handleErrorMessage(attributeTypes.state.codeName)}
                                  inputLabel={attributeTypes.state.label!}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, lg: 3 }}>
                            <TextField
                              required
                              error={!!errorsByCodeName[attributeTypes.zip.codeName]}
                              helperText={handleErrorMessage(attributeTypes.zip.codeName)}
                              inputLabel={attributeTypes.zip.label}
                              value={fieldValues.zip.value}
                              onChangeInput={(value) => handleInputFieldChange(attributeTypes.zip.key, value)}
                              handleOnPattern={getNumbers}
                              fullWidth
                              disabled={isLioSales || fieldsDisabled}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        size={{ xs: 12 }}
                        sx={{
                          width: '100%',
                          flexDirection: 'row',
                          gap: 3,
                          // justifyContent: 'space-between',
                        }}
                      >
                        <Grid size={{ xs: 12, sm: 5, md: 5, lg: 4, xl: 4 }} sx={{ marginBottom: 2 }}>
                          <TextField
                            error={!!errorsByCodeName[attributeTypes.emailAddress.codeName] || !isEmailValid}
                            helperText={
                              isEmailValid
                                ? handleErrorMessage(attributeTypes.emailAddress.codeName)
                                : 'Must be a valid email address'
                            }
                            inputLabel={attributeTypes.emailAddress.label}
                            value={fieldValues.emailAddress.value}
                            onChangeInput={(value) => handleInputFieldChange(attributeTypes.emailAddress.key, value)}
                            fullWidth
                            disabled={isLioSales || fieldsDisabled}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5, md: 3, lg: 3 }} sx={{ marginBottom: 2 }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              position: 'relative',
                              alignItems: 'right',
                              width: '100%',
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.subdued',
                              }}
                            >
                              Phone
                            </Typography>
                            <StyledIMaskInput
                              required
                              mask='+{1} (000) 000-0000'
                              onAccept={(value) => handleInputFieldChange(attributeTypes.phone.key, value)}
                              value={fieldValues.phone.value}
                              disabled={isLioSales || fieldsDisabled}
                              sx={getMaskedStyles(false)}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        sx={{
                          marginTop: '20px',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          variant='outlined'
                          isLoading={isQuoteItemAttributesLoading}
                          disabled={!hasUnsavedChanges || isNotValid}
                          onClick={onSaveQuote}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                ),
              }
            : {},
        ]}
      </BindCard>
    </Box>
  );
};
